import type { AuditRow, Db } from "../data/db.ts";
import { action, date, deleteButton, h, mount, notice, table } from "./dom.ts";

export const STATUS_LABEL: Record<AuditRow["status"], string> = {
  collecting: "Waiting for data",
  data_checked: "Data checked",
  analysed: "Analysed",
  reported: "Report drafted",
  implementing: "Implementing",
  closed: "Closed",
};

export function statusPill(s: AuditRow["status"]): HTMLElement {
  return h("span", { class: `pill pill-${s}` }, STATUS_LABEL[s]);
}

export async function auditsView(db: Db): Promise<HTMLElement> {
  const [audits, clients] = await Promise.all([db.listAudits(), db.listClients()]);
  const root = h("section", { class: "page" });
  const unused = clients.filter((c) => !audits.some((a) => a.client_id === c.id));

  const clientSel = h("select", { id: "new-client", "aria-label": "Client" },
    h("option", { value: "" }, "New client…"),
    clients.map((c) => h("option", { value: c.id }, c.name)),
  );
  const clientName = h("input", { type: "text", id: "new-client-name", placeholder: "Client name", "aria-label": "New client name", maxlength: 200 });
  clientSel.addEventListener("change", () => (clientName.hidden = clientSel.value !== ""));
  const title = h("input", { type: "text", id: "new-title", placeholder: "e.g. Shipping and packaging audit, Q4 2026", "aria-label": "Audit title", maxlength: 200 });

  const create = action("Create audit", async () => {
    let clientId = clientSel.value;
    if (!clientId) {
      if (!clientName.value.trim()) throw new Error("Enter a client name");
      clientId = (await db.createClient(clientName.value)).id;
    }
    if (!title.value.trim()) throw new Error("Enter an audit title");
    const a = await db.createAudit(clientId, title.value);
    location.hash = `#/audit/${a.id}`;
  }, { primary: true });

  const rows = audits.map((a) => [
    h("a", { href: `#/audit/${a.id}` }, a.client_name),
    a.title,
    statusPill(a.status),
    date(a.data_complete_at),
    a.report_due ? h("strong", null, date(a.report_due)) : "–",
  ]);

  mount(root,
    h("h1", null, "Audits"),
    h("details", { class: "card", open: audits.length === 0 },
      h("summary", null, "New audit"),
      h("div", { class: "form-grid" },
        h("label", { for: "new-client" }, "Client"), h("div", null, clientSel, " ", clientName),
        h("label", { for: "new-title" }, "Audit"), title,
        h("span"), create,
      ),
    ),
    audits.length === 0
      ? notice("info", "No audits yet. Create the first one above.")
      : table(["Client", "Audit", "Status", "Data complete", "Report due"], rows),
    unused.length
      ? h("details", { class: "card" }, h("summary", null, `Clients with no audits (${unused.length})`),
          h("p", { class: "muted small" }, "Left when an audit is deleted, or created and never used. Delete any you no longer need; a lead that became the client is kept."),
          table(["Client", ""], unused.map((c) => [c.name, deleteButton(c.name, `Delete the client ${c.name}?`, async () => {
            await db.deleteClient(c.id);
            window.dispatchEvent(new HashChangeEvent("hashchange"));
          })])))
      : "",
  );
  return root;
}
